import { useRef } from 'react';
import { Box } from '@mui/material';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { SAFE_AREA_BOTTOM, SAFE_AREA_TOP } from 'core/theme.tokens';

gsap.registerPlugin(useGSAP);

type AuthBootSplashProps = {
  logoSrc: string;
};

export function AuthBootSplash({ logoSrc }: AuthBootSplashProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useGSAP(
    () => {
      const logo = logoRef.current;
      if (!logo) return;

      if (reduceMotion) {
        gsap.set(logo, { autoAlpha: 1, scale: 1 });
        return;
      }

      gsap.fromTo(
        logo,
        { autoAlpha: 0, scale: 0.94 },
        { autoAlpha: 1, scale: 1, duration: 0.28, ease: 'power2.out' },
      );
    },
    { scope: rootRef, dependencies: [reduceMotion], revertOnUpdate: true },
  );

  return (
    <Box
      ref={rootRef}
      role="status"
      aria-live="polite"
      aria-label="Opening shop"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100dvh',
        bgcolor: '#F4F1EB',
        pt: SAFE_AREA_TOP,
        pb: SAFE_AREA_BOTTOM,
      }}
    >
      <Box
        component="img"
        ref={logoRef}
        src={logoSrc}
        alt="Optic Truck Works"
        width={120}
        height={120}
        sx={{ width: { xs: 96, sm: 120 }, height: 'auto', willChange: 'transform' }}
      />
    </Box>
  );
}
